import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { Button, Stack, Typography } from "@mui/material";

import { LoadingView } from "../../components/LoadingView";
import { getUserStore, type FrontendId } from "../../store/UserStore";
import ResendVerificationLinkButton from "./ResendVerificationLinkButton";
import ErrorPane from "./ErrorPane";
import { GoToLoginButton } from "./GoToLoginButton";

const VerifyEmail = observer(
  ({
    pathLogin,
    frontendId,
  }: {
    pathLogin: string;
    frontendId: FrontendId;
  }) => {
    const { verifyEmail, error } = getUserStore(frontendId);
    const { t } = useTranslation();
    const [status, setStatus] = useState<
      "isVerifying" | "success" | "error" | "emailSent"
    >("isVerifying");
    const [params] = useSearchParams();
    const navigate = useNavigate();

    useEffect(() => {
      const verify = async () => {
        const res = await verifyEmail(params.get("token"));
        setStatus(res ? "success" : "error");
      };

      verify();
    }, [params, verifyEmail]);

    return (
      <Stack width={"min(26rem, 80vw)"} position={"relative"} gap={2}>
        {status === "isVerifying" && <LoadingView />}

        {status === "success" && (
          <>
            <Typography>{t("login.verificationSuccess")}</Typography>
            <Button onClick={() => navigate(pathLogin)} variant="contained">
              {t("continue")}
            </Button>
          </>
        )}

        {status === "error" && (
          <>
            <Typography>{t("login.linkError")}</Typography>
            <ResendVerificationLinkButton
              email={params.get("email")}
              onSuccess={() => setStatus("emailSent")}
              frontendId={frontendId}
            />
            <GoToLoginButton path={pathLogin} />
            <ErrorPane error={error} />
          </>
        )}

        {status === "emailSent" && (
          <>
            <Typography>
              {t("login.verificationLinkSent", { email: params.get("email") })}
            </Typography>
            <ResendVerificationLinkButton
              email={params.get("email")}
              onSuccess={() => setStatus("emailSent")}
              frontendId={frontendId}
            />
            <GoToLoginButton path={pathLogin} />
          </>
        )}
      </Stack>
    );
  }
);

export default VerifyEmail;
